import type { FlowSort } from '@shared/flow/view';
import type { FlowPlaysRow, PlaysGroupBy, PlaysGroupKey } from '@shared/flow/plays-view';
import { PLAYS_GROUP_BY_NONE, PLAY_SLUG_GROUP, PLAY_TYPE_GROUPS, type PlayTypeGroup } from '@shared/flow/plays-view';

/**
 * Row ORDERING for the Plays `FlowTable` mount (plan
 * `plans/2026-09-11-004-plays-tab-qa-round2-dispatch.md` U17, amendment
 * **A11**). Replaces `groupPlays.ts`, which returned nested
 * `{ label, rows }` buckets for the bespoke accordion this unit deletes.
 *
 * `FlowTable` renders group headers off ROW CONTIGUITY: it walks the flat
 * `rows` array and emits a header wherever the group value changes
 * (`FlowTable.tsx` :961-963). So grouping here is not a data structure, it is
 * a SORT — group key first (in the product's display order), row sort second.
 * That is also why the Flows "placeholder" group is gone: a group with no
 * rows cannot exist in a contiguity model, so the withheld ~660 Gong flows
 * are surfaced as `hasMore`/`loadMoreLabel` instead (dispatch decision,
 * open question 3).
 *
 * Pure — no React, no fetch. `PlaysSubtab` is the only consumer.
 */

/**
 * The value a row groups under, for ONE group key. Shared verbatim with the
 * table config's `groupValue` hook so the header text the rep reads and the
 * order the rows arrive in can never be derived two different ways.
 *
 * `play_type` folds the row's SLUG into its product group label (Events /
 * Flows / Intros / Messages), falling back to the play type's own name for a
 * slug outside that taxonomy. This is the whole of amendment **A15**: the
 * old accordion rendered the raw exposure slug (`sfdc_visible_flag`) beside
 * the count, which is a column in a database, not a word a rep knows.
 *
 * Every other key returns the row's RAW value — what the header says is what
 * is in the cell (never a derived bucket; the retired `bucket` column is
 * exactly the mistake not to repeat). `null` is passed through for
 * `FlowTable`'s own `groupValueOf` to render as an em dash.
 */
export function playsGroupValueOf(row: FlowPlaysRow, groupBy: string): string | null {
  switch (groupBy as PlaysGroupKey) {
    case 'play_type':
      return PLAY_SLUG_GROUP[row.play_type] ?? row.play_type_name;
    case 'status':
      return row.status;
    case 'city':
      return row.city;
    default:
      return null;
  }
}

/** The label a null `status`/`city` groups under — the same em dash `FlowTable`'s `groupValueOf` renders, so the two agree on the header text. */
const NULL_GROUP_LABEL = '—';

/**
 * The group HEADER TEXT for a row — `playsGroupValueOf` narrowed to a string
 * by exactly the rule `FlowTable`'s own private `groupValueOf`
 * (`FlowTable.tsx` :1163) applies, so the order this module sorts by and the
 * header the table renders can never disagree. Exported for the test that
 * pins that agreement.
 */
export function playsGroupLabelOf(row: FlowPlaysRow, groupBy: string): string {
  const raw = playsGroupValueOf(row, groupBy);
  return raw == null || raw === '' ? NULL_GROUP_LABEL : raw;
}

/**
 * Group display order.
 *
 * `play_type` has a FIXED product order (`PLAY_TYPE_GROUPS`) with unknown
 * play types after it, alphabetically. Every other key is plain A→Z. In both
 * cases the em-dash ("no value") group sorts ALWAYS last, never
 * alphabetically between `Austin` and `Zurich` — "no value" is not a value.
 */
function compareGroups(a: string, b: string, groupBy: string): number {
  if (a === b) return 0;
  if (a === NULL_GROUP_LABEL) return 1;
  if (b === NULL_GROUP_LABEL) return -1;
  if (groupBy === 'play_type') {
    const ai = PLAY_TYPE_GROUPS.indexOf(a as PlayTypeGroup);
    const bi = PLAY_TYPE_GROUPS.indexOf(b as PlayTypeGroup);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
  }
  return a.localeCompare(b);
}

function sortValueOf(row: FlowPlaysRow, field: string): string | number | null {
  switch (field) {
    case 'name':
      return row.name;
    case 'start_date':
      return row.start_date;
    case 'capacity':
      return row.capacity;
    case 'suggested':
      return row.counts.suggested;
    case 'invited_sent':
      return row.counts.invited_sent;
    case 'responded':
      return row.counts.responded;
    default:
      return null;
  }
}

/**
 * Nulls always sort last, regardless of `dir` — a null Suggested/Responded
 * (non-event plays) or a null start_date is "not applicable", not
 * "smallest", so DESC must not float it to the top. Ported verbatim from
 * `sortPlays` (`groupPlays.ts`, deleted by this unit).
 */
function compareRows(a: FlowPlaysRow, b: FlowPlaysRow, sort: FlowSort): number {
  const av = sortValueOf(a, sort.field);
  const bv = sortValueOf(b, sort.field);
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  const cmp = typeof av === 'string' && typeof bv === 'string' ? av.localeCompare(bv) : Number(av) - Number(bv);
  return sort.dir === 'DESC' ? -cmp : cmp;
}

/**
 * The flat, `FlowTable`-ready row order: rows bucketed by `groupBy` in group
 * display order, each bucket internally ordered by `sort`. `groupBy` of
 * `'none'` (the stored ungrouped sentinel) or `undefined` skips the group
 * pass entirely and returns one globally sorted list.
 *
 * Stable by construction: a single `Array.prototype.sort` over a copy with a
 * comparator that falls through from group to row, so two rows in the same
 * group with equal sort values keep their payload order.
 */
export function orderPlaysForTable(
  rows: FlowPlaysRow[],
  groupBy: PlaysGroupBy | undefined,
  sort: FlowSort,
): FlowPlaysRow[] {
  const grouping = groupBy && groupBy !== PLAYS_GROUP_BY_NONE ? groupBy : null;
  return [...rows].sort((a, b) => {
    if (grouping) {
      const groupCmp = compareGroups(playsGroupLabelOf(a, grouping), playsGroupLabelOf(b, grouping), grouping);
      if (groupCmp !== 0) return groupCmp;
    }
    return compareRows(a, b, sort);
  });
}
